import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useBasePath } from "@/hooks/useBasePath";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { Search, ShieldCheck, ChevronRight, Download, FileSpreadsheet, FileCode } from "lucide-react";
import { Button } from "@/components/ui/button";

const fileNameFrom = (res, fallback) => {
  const m = /filename="?([^";]+)"?/i.exec(res.headers?.["content-disposition"] || "");
  return m ? m[1] : fallback;
};

export const downloadFromApi = async (url, fallbackName) => {
  try {
    const res = await API.get(url, { responseType: "blob" });
    const href = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = href;
    a.download = fileNameFrom(res, fallbackName);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(href);
  } catch (err) {
    let msg = "Download failed";
    try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch (_) { /* non-JSON error body */ }
    toast.error(msg);
  }
};

export const downloadSectionPdf = (section, investorId) =>
  downloadFromApi(`/compliance/${section}/${investorId}/pdf`, `${section.toUpperCase()}_extracted.pdf`);

export const SectionPdfButton = ({ section, investorId, hasSource, testId, compact }) => (
  <span className={`inline-block ${hasSource ? "" : "cursor-not-allowed"}`} onClick={(e) => e.stopPropagation()}
    title={hasSource ? `Download extracted ${section.toUpperCase()} pages` : "Original file not stored (uploaded before file storage was enabled)"}>
    <Button variant={compact ? "ghost" : "secondary"} size={compact ? "icon" : "sm"} disabled={!hasSource}
      className={compact ? "w-8 h-8" : "border border-border"} data-testid={testId}
      onClick={() => downloadSectionPdf(section, investorId)}>
      <Download className="w-4 h-4" />{!compact && <span className="ml-1">Download PDF</span>}
    </Button>
  </span>
);

export const SECTION_META = {
  fatca: { title: "FATCA", subtitle: "W-8BEN / W-8BEN-E / W-9 details saved from each investor's FATCA / CRS upload" },
  crs: { title: "CRS", subtitle: "Individual and entity CRS self-certification details saved from each investor's FATCA / CRS upload" },
};

export const ComplianceStatusBadge = ({ status, testId }) => (
  <Badge variant="outline" data-testid={testId}
    className={`text-[10px] uppercase tracking-wider ${status === "complete" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-amber-500/10 text-amber-400 border-amber-500/20"}`}>
    {status === "complete" ? "Complete" : "Not uploaded"}
  </Badge>
);

const MissingCell = ({ row }) => {
  if (row.status !== "complete") return <span className="text-xs text-muted-foreground">—</span>;
  if (!row.missing_fields.length) return <span className="text-xs text-emerald-400">None</span>;
  return (
    <span className="text-xs text-amber-400" title={row.missing_fields.join(", ")} data-testid={`missing-${row.id}`}>
      {row.missing_fields.length} missing
    </span>
  );
};

const StatCard = ({ label, value, testId }) => (
  <Card><CardContent className="p-4">
    <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
    <p className="font-heading text-2xl font-bold mt-1" data-testid={testId}>{value}</p>
  </CardContent></Card>
);

export default function ComplianceListPage({ section }) {
  const navigate = useNavigate();
  const { fundId } = useParams();
  const basePath = useBasePath();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const meta = SECTION_META[section];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/compliance/${section}${fundId ? `?fund_id=${fundId}` : ""}`);
      setRows(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to load ${meta.title} list`);
    } finally {
      setLoading(false);
    }
  }, [section, fundId, meta.title]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = rows.filter(r => !q || r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
  const complete = rows.filter(r => r.status === "complete").length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid={`compliance-${section}-page`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
            <ShieldCheck className="w-7 h-7 text-primary" /> Compliance — {meta.title}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{meta.subtitle}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" className="border border-border" data-testid="export-excel-btn" disabled={!rows.length}
            onClick={() => downloadFromApi(`/compliance/${section}/export?format=xlsx${fundId ? `&fund_id=${fundId}` : ""}`, `${meta.title}_compliance.xlsx`)}>
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Export Excel
          </Button>
          <Button variant="secondary" className="border border-border" data-testid="export-xml-btn" disabled={!rows.length}
            onClick={() => downloadFromApi(`/compliance/${section}/export?format=xml${fundId ? `&fund_id=${fundId}` : ""}`, `${meta.title}_compliance.xml`)}>
            <FileCode className="w-4 h-4 mr-2" /> Export XML
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total Investors" value={rows.length} testId="stat-total" />
        <StatCard label="Complete" value={complete} testId="stat-complete" />
        <StatCard label="Not Uploaded" value={rows.length - complete} testId="stat-not-uploaded" />
      </div>
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="relative max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email" className="pl-9 bg-secondary/50" data-testid="compliance-search-input" />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Investor</TableHead><TableHead>Investor ID</TableHead><TableHead>Fund</TableHead>
                <TableHead>Type</TableHead><TableHead>Form</TableHead><TableHead>Status</TableHead>
                <TableHead>Missing Data</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground" data-testid="compliance-empty">No investors found</TableCell></TableRow>
              )}
              {!loading && filtered.map(r => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-secondary/40" data-testid={`compliance-row-${r.id}`}
                  onClick={() => navigate(`${basePath}/compliance/${section}/${r.id}`)}>
                  <TableCell><p className="font-medium text-sm">{r.name}</p><p className="text-xs text-muted-foreground">{r.email}</p></TableCell>
                  <TableCell className="font-mono text-xs">{r.investor_display_id}</TableCell>
                  <TableCell className="text-sm">{r.fund_name || "—"}</TableCell>
                  <TableCell className="text-sm">{r.investor_type || "—"}</TableCell>
                  <TableCell className="text-sm">{r.form_type || "—"}</TableCell>
                  <TableCell><ComplianceStatusBadge status={r.status} testId={`status-${r.id}`} /></TableCell>
                  <TableCell><MissingCell row={r} /></TableCell>
                  <TableCell className="whitespace-nowrap">
                    <SectionPdfButton section={section} investorId={r.id} hasSource={r.has_source} compact testId={`download-pdf-${r.id}`} />
                    <ChevronRight className="w-4 h-4 text-muted-foreground inline ml-1" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
